/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */
import isTruthy from '@src/lib/isTruthy'

import type Service from '.'
import type { Identity, MemberModel } from './model'
import type { ContactModel } from './model/contact/ContactModel'
import type MainWorker from './worker/main'

export interface SearchResult<I extends Identity = Identity> {
  identity: I
  type: 'contact' | 'member'
  matchedPhoneNumber?: string
}

export default class SearchStore {
  constructor(
    private root: Service,
    private worker: MainWorker,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.worker.service.search.hydrate()
  }

  async searchFilterContactsWithIdentities(
    query: string,
    limit = 20,
  ): Promise<SearchResult[]> {
    const [contactQueryResult, memberQueryResult] = await Promise.all([
      // Search contacts using the API
      this.root.transport.search.searchFilterContacts({
        query,
        maxResults: limit,
      }),
      // Search members using the worker service (from local database)
      this.worker.service.search.identities(query, limit, 'members'),
    ])

    // Get contacts from API
    const contactResults = await Promise.all(
      contactQueryResult.data.map(async (contactData) => {
        // getById() will automatically fetch from API if contact doesn't exist in local storage
        const identity = await this.root.contact.getById(contactData.entity.entityId)

        if (identity) {
          return {
            identity,
            type: 'contact' as const,
            matchedPhoneNumber: '',
          }
        }

        // If getById returns null, the contact doesn't exist
        return null
      }),
    )

    // Fetch members from local database
    const memberResults = await Promise.all(
      memberQueryResult.map(async (memberResult) => {
        if (memberResult.t === 'member') {
          // Load the member from the collection to ensure it's properly hydrated
          await this.root.member.collection.load([memberResult.i])
          const identity = this.root.member.collection.get(memberResult.i.id)

          if (identity) {
            return {
              identity,
              type: 'member' as const,
              matchedPhoneNumber: memberResult.m,
            }
          }
        }

        return null
      }),
    )

    // Combine and filter contacts and members results
    return (
      [...contactResults, ...memberResults]
        .filter(isTruthy)
        // Only return the first set of results up to the limit
        .slice(0, limit)
    )
  }

  async identities(
    query: string,
    limit: number,
    type: 'members',
  ): Promise<SearchResult<MemberModel>[]>
  async identities(
    query: string,
    limit: number,
    type: 'contacts',
  ): Promise<SearchResult<ContactModel>[]>
  async identities(
    query: string,
    limit?: number,
    type?: 'all',
  ): Promise<SearchResult<ContactModel | MemberModel>[]>
  async identities(
    query: string,
    limit = 20,
    type: 'all' | 'members' | 'contacts' = 'all',
  ): Promise<SearchResult<ContactModel | MemberModel>[]> {
    const [contactsResult, membersResult] = await Promise.all([
      // Fetch contacts from REST endpoint
      this.root.transport.search.genericContactSearch({
        query,
        maxResults: limit,
        includeWhere: { contacts: true },
      }),
      // Members are still searched locally
      type === 'all'
        ? this.worker.service.search.identities(query, limit, 'members')
        : Promise.resolve([]),
    ])

    // Extract contact IDs and member data
    const contactIds = contactsResult.data.map(
      (contactData) => contactData.entity.entityId,
    )

    const members = membersResult.filter((r) => r.t === 'member').map((r) => r.i)

    // Batch load contacts and members
    await Promise.all([
      this.root.contact.fetchContactsByIds(contactIds), // this will get from the API if not available locally
      this.root.member.collection.load(members),
    ])

    // Map contacts from REST response
    const contactResults = contactsResult.data.map((contactData) => {
      // create an identity from the contact data
      const identity = this.root.contact.get(contactData.entity.entityId)

      if (!identity) {
        return null
      }

      return {
        matchedPhoneNumber: contactData.entity.phoneNumbers[0] ?? '',
        type: 'contact' as const,
        identity,
      }
    })

    // Map members from local search
    const memberResults = membersResult.map((r) => {
      if (r.t !== 'member') {
        return null
      }

      const identity = this.root.member.collection.get(r.i.id)

      if (!identity) {
        return null
      }

      return {
        matchedPhoneNumber: r.m,
        type: 'member' as const,
        identity,
      }
    })

    return [...contactResults, ...memberResults].filter(isTruthy)
  }

  companies(query: string, limit = 10): Promise<string[]> {
    return this.worker.service.search.companies(query, limit)
  }
}
